<template>
  <ModalComponent
    :submitButtonText="$t('portfolios.confirm_merge')"
    :submit="handleMerge"
    :submitDisabled="!mergeTargetPortfolioId || isProcessing"
    :hideSubmitButton="true"
    @close="close"
    @setForceToggle="forceToggle = $event"
  >
    <template #title>
      <FontAwesomeIcon icon="fas fa-compress-alt" class="text-primary me-2" />
      {{ $t("portfolios.merge_modal_title") }}
    </template>
    <template #body>
      <div class="d-flex flex-column gap-3 mb-4">
        <!-- Source Card -->
        <div class="card bg-light border-primary-subtle">
          <div class="card-body p-3">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <small class="text-uppercase text-primary fw-bold">{{
                $t("portfolios.source_portfolio")
              }}</small>
            </div>
            <h6 class="fw-bold text-primary mb-1 text-break">
              {{ sourcePortfolio?.name }}
            </h6>
            <div class="small text-muted">
              {{
                $t("portfolios.volume_and_items", {
                  volume: sourcePortfolio?.total_volume,
                  count: sourcePortfolio?.items.length,
                })
              }}
            </div>
          </div>
        </div>

        <!-- Arrow Down -->
        <div class="text-center text-muted">
          <FontAwesomeIcon icon="fas fa-arrow-down" size="lg" />
        </div>

        <!-- Target Selection -->
        <div>
          <label
            class="form-label fw-bold small text-uppercase text-muted mb-1"
          >
            {{ $t("portfolios.target_portfolio") }}
          </label>
          <select
            v-model="mergeTargetPortfolioId"
            class="form-select form-select-lg"
            :class="{
              'border-primary': mergeTargetPortfolioId,
              'text-muted': !mergeTargetPortfolioId,
            }"
          >
            <option :value="null" disabled selected>
              {{ $t("portfolios.select_target_placeholder") }}
            </option>
            <option v-for="p in availableTargets" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Warning / Confirmation -->
      <div
        class="alert"
        :class="mergeTargetPortfolioId ? 'alert-warning' : 'alert-light border'"
      >
        <div class="d-flex gap-3 align-items-start">
          <FontAwesomeIcon
            :icon="
              mergeTargetPortfolioId
                ? 'fas fa-exclamation-triangle'
                : 'fas fa-info-circle'
            "
            class="mt-1"
          />
          <div class="small">
            <span v-if="selectedTargetPortfolio">
              {{
                $t("portfolios.merge_confirm_warning", {
                  source: sourcePortfolio?.name,
                  target: selectedTargetPortfolio.name,
                })
              }}
            </span>
            <span v-else class="text-muted">
              {{ $t("portfolios.merge_info") }}
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="d-grid gap-2 w-100">
        <button
          type="button"
          class="btn btn-primary btn-lg"
          :disabled="!mergeTargetPortfolioId || isProcessing"
          @click="handleMerge"
        >
          <span
            v-if="isProcessing"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          {{ $t("portfolios.confirm_merge") }}
        </button>
        <button
          type="button"
          class="btn btn-link text-muted text-decoration-none"
          @click="close"
        >
          {{ $t("common.cancel") }}
        </button>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { PortfolioGroup } from "../types";

const model = defineModel<boolean>({ required: true });

const props = defineProps<{
  sourcePortfolio: PortfolioGroup | null;
  availableTargets: PortfolioGroup[];
}>();

const emit = defineEmits<{
  success: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const forceToggle = ref<(value: boolean) => void>();
const mergeTargetPortfolioId = ref<number | null>(null);
const isProcessing = ref(false);

const selectedTargetPortfolio = computed(() => {
  if (!mergeTargetPortfolioId.value) return null;
  return props.availableTargets.find(
    (p) => p.id === mergeTargetPortfolioId.value
  );
});

const close = () => {
  model.value = false;
};

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) {
    mergeTargetPortfolioId.value = null;
    isProcessing.value = false;
  }
});

const handleMerge = async () => {
  if (!props.sourcePortfolio || !mergeTargetPortfolioId.value) return;

  isProcessing.value = true;
  try {
    await goSolidApi.post(`/api/portfolio/${props.sourcePortfolio.id}/merge`, {
      target_portfolio_id: mergeTargetPortfolioId.value,
    });
    globalSuccessHandler(t("common.success"), t("common.saved_successfully"));
    emit("success");
    close();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
};
</script>
